import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaInstagram } from 'react-icons/fa';
import {
  MapPinIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  ClockIcon,
  EyeIcon,
  PlusIcon,
  ArrowRightIcon,
  ShareIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { PlayIcon } from '@heroicons/react/24/solid';
import { Menu, Transition } from '@headlessui/react';
import ShareModal from '../common/ShareModal';
import { Collaboration } from '../../types';
import CollaborationShowcaseModal from './CollaborationShowcaseModal';
import Avatar from '../common/Avatar';
import { getOptimizedImageUrl, resolveImageUrl } from '../../utils/image';
import { formatCurrency } from '../../utils/format';
import CreateCollaborationModal from './CreateCollaborationModal';
import { collaborationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ConfirmationModal from '../common/ConfirmationModal';

import { useAppSelector } from '../../hooks/redux';

const getProjectVideoThumbnail = (urlStr: string) => {
  try {
    const urlObj = new URL(urlStr);
    const host = urlObj.hostname.toLowerCase();
    const path = urlObj.pathname;
    if (host === 'youtu.be') return `https://img.youtube.com/vi/${path.slice(1)}/hqdefault.jpg`;
    if (host.includes('youtube.com')) {
      const v = urlObj.searchParams.get('v');
      if (v) return `https://img.youtube.com/vi/${v}/hqdefault.jpg`;
    }
    if (host.includes('drive.google.com')) {
      let fileId = "";
      const parts = path.split('/').filter(Boolean);
      for (let i = 0; i < parts.length; i++) {
        if (parts[i] === 'd' && i + 1 < parts.length) {
          fileId = parts[i+1];
          break;
        }
      }
      if (!fileId) {
        const idParam = urlObj.searchParams.get('id');
        if (idParam) fileId = idParam;
      }
      if (fileId) {
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w600`;
      }
    }
  } catch (e) {}
  return '';
};

const InstagramIcon = FaInstagram as any;

interface CollaborationGridProps {
  collaborations: Collaboration[];
  loading?: boolean;
  selectedCollabId?: string;
  isOwnProfile?: boolean;
  onCreateCollaboration?: () => void;
  onSyncInstagram?: () => void;
  onCloseModal?: () => void;
  onSelect?: (id: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onUpdate?: () => void;
}

const CollaborationGrid: React.FC<CollaborationGridProps> = ({
  collaborations,
  loading = false,
  selectedCollabId,
  isOwnProfile = false,
  onCreateCollaboration,
  onSyncInstagram,
  onCloseModal,
  onSelect,
  hasMore = false,
  onLoadMore,
  onUpdate
}) => {
  const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
  const [shareItem, setShareItem] = useState<Collaboration | null>(null);
  const [editingCollaboration, setEditingCollaboration] = useState<Collaboration | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<'next' | null>(null);
  const [collabToDelete, setCollabToDelete] = useState<string | null>(null);
  const [deletingCollab, setDeletingCollab] = useState<boolean>(false);
  
  const { user: currentUser } = useAppSelector((state) => state.auth);
  
  const visibleCollaborations = collaborations.filter(c => {
    // 1. Profile owner sees everything
    if (isOwnProfile) return true;
    
    // 2. Creator sees everything (redundant with 1 but explicit)
    if (c.creator_id === currentUser?.id) return true;
    
    // 3. Collaborators see the project
    if (c.collaborators?.some(collab => collab.user_id === currentUser?.id)) return true;
    
    // 4. Public/Open projects are visible to everyone
    if (c.status === 'open') return true;
    
    // 5. Published/Completed/Approved collaborations are publicly visible (for portfolio showcase)
    if (['published', 'completed', 'approved'].includes(c.status)) return true;
    
    return false;
  });

  const prevCollaborationsLengthRef = React.useRef(visibleCollaborations.length);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const collabRefs = React.useRef<{ [key: string]: HTMLDivElement }>({});

  useEffect(() => {
    if (pendingNavigation === 'next' && visibleCollaborations.length > prevCollaborationsLengthRef.current) {
        // New items arrived
        const nextIndex = prevCollaborationsLengthRef.current;
        if (visibleCollaborations[nextIndex]) {
            setSelectedCollaboration(visibleCollaborations[nextIndex]);
        }
        setPendingNavigation(null);
    }
    prevCollaborationsLengthRef.current = visibleCollaborations.length;
  }, [visibleCollaborations, pendingNavigation]);

  useEffect(() => {
    // console.log("CollaborationGrid Effect:", { selectedCollabId, visibleCount: visibleCollaborations.length });
    if(selectedCollabId && visibleCollaborations.length > 0) {
      // Find and set selected collaboration
      const targetCollab = visibleCollaborations.find(c => c.id === selectedCollabId);
      if (targetCollab) {
        setSelectedCollaboration(targetCollab);
      }

      // Scroll to the card
      if (visibleCollaborations.length > 0) {
        let attempts = 0;
        const maxAttempts = 5;
        
        const tryScroll = () => {
          const element = collabRefs.current[selectedCollabId];
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(tryScroll, 500);
          }
        };
        tryScroll();
      }
    } else if (!selectedCollabId) {
      setSelectedCollaboration(null);
    }
  }, [selectedCollabId, visibleCollaborations])

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !onLoadMore) return;
    
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          setLoadingMore(true);
          onLoadMore();
          // Reset loadingMore after a delay
          setTimeout(() => setLoadingMore(false), 1000);
        }
      },
      { threshold: 0.1, rootMargin: '800px' }
    );
    
    observerRef.current.observe(sentinel);
    
    return () => observerRef.current?.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  const handleDelete = (id: string) => {
    setCollabToDelete(id);
  };

  if (loading && collaborations.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-900 rounded-lg shadow-md p-6 animate-pulse border border-gray-100 dark:border-gray-800">
            <div className="h-48 bg-gray-200 dark:bg-gray-800 rounded-lg mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded mb-4"></div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const handleCloseModal = () => {
    setSelectedCollaboration(null);
    if (onCloseModal) {
      onCloseModal();
    }
  };

  const currentIndex = selectedCollaboration ? visibleCollaborations.findIndex(c => c.id === selectedCollaboration.id) : -1;
  const hasNext = currentIndex < visibleCollaborations.length - 1 || (hasMore && !loadingMore);
  const hasPrevious = currentIndex > 0;

  const handleNextCollaboration = () => {
      if (currentIndex < visibleCollaborations.length - 1) {
          const nextCollab = visibleCollaborations[currentIndex + 1];
          if (onSelect) {
            onSelect(nextCollab.id);
          } else {
            setSelectedCollaboration(nextCollab);
          }
      } else if (hasMore && onLoadMore && !loadingMore) {
          setPendingNavigation('next');
          onLoadMore();
      }
  };

  const handlePreviousCollaboration = () => {
      if (currentIndex > 0) {
          const prevCollab = visibleCollaborations[currentIndex - 1];
          if (onSelect) {
            onSelect(prevCollab.id);
          } else {
            setSelectedCollaboration(prevCollab);
          }
      }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-8">
        {/* Create Collaboration Card */}
        {onCreateCollaboration && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onCreateCollaboration}
            className="group cursor-pointer bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-black dark:hover:border-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 flex flex-col items-center justify-center text-center h-full min-h-[300px]"
          >
            <div className="bg-white dark:bg-gray-900 p-5 rounded-full shadow-sm group-hover:shadow-md transition-all mb-4 group-hover:scale-110 transform">
              <PlusIcon className="h-8 w-8 text-gray-400 group-hover:text-black dark:group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-black dark:group-hover:text-white transition-colors mb-1">
              Create Project
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors px-4">
              Post your project and invite collaborators
            </p>
          </motion.div>
        )}



        {visibleCollaborations.length === 0 && !loading && !onCreateCollaboration ? (
          <div className="col-span-full text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
            <div className="flex justify-center mb-4">
              <UserGroupIcon className="w-10 h-10 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No Projects Yet</h3>
            <p className="text-gray-500 dark:text-gray-400">This user hasn't posted any projects.</p>
          </div>
        ) : (
        visibleCollaborations.map((collaboration, index) => {
          const isHighlighted = collaboration.id === selectedCollabId;
          const isCreator = currentUser?.id === collaboration.creator_id;
          
          return (
          <motion.div
            key={collaboration.id}
            ref={(el) => { if (el) collabRefs.current[collaboration.id] = el; }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              scale: isHighlighted ? [1, 1.02, 1] : 1,
              borderColor: isHighlighted ? ['#e5e7eb', '#000000', '#e5e7eb'] : '#f3f4f6',
              boxShadow: isHighlighted ? "0 0 0 2px rgba(0, 0, 0, 0.1)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
            }}
            transition={{ 
              duration: 0.4, 
              delay: index * 0.05,
              scale: { duration: 0.5, times: [0, 0.5, 1] },
              borderColor: { duration: 2, repeat: isHighlighted ? 1 : 0 }
            }}
            className={`bg-white dark:bg-gray-900 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group relative border h-full flex flex-col ${isHighlighted ? 'border-black dark:border-white ring-4 ring-gray-100 dark:ring-gray-800' : 'border-gray-100 dark:border-gray-800'}`}
            onClick={() => {
              if (onSelect) {
                onSelect(collaboration.id);
              } else {
                setSelectedCollaboration(collaboration);
              }
            }}
          >
            {/* Image Section */}
            <div className="relative h-48 overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800">
              {(() => {
                // Combine cover image and other images (use thumbnails if available)
                const coverImg = collaboration.thumbnail_cover_image_signed || (collaboration.thumbnail_cover_image ? resolveImageUrl(collaboration.thumbnail_cover_image) : getOptimizedImageUrl(collaboration.cover_image, 'thumbnail'));
                
                // Debug logging for new collaborations
                if (index === 0) {
                    // console.log('CollaborationGrid Debug:', {
                    //     id: collaboration.id,
                    //     title: collaboration.title,
                    //     thumbnail_cover_image: collaboration.thumbnail_cover_image,
                    //     cover_image: collaboration.cover_image,
                    //     resolvedCoverImg: coverImg
                    // });
                }
                let imgs;

                if (collaboration.thumbnail_images_signed && collaboration.thumbnail_images_signed.length > 0) {
                  imgs = collaboration.thumbnail_images_signed;
                } 
                else if (collaboration.images_signed && collaboration.images_signed.length > 0) {
                  imgs = collaboration.images_signed;
                } 
                else if (collaboration.thumbnail_images && collaboration.thumbnail_images.length > 0) {
                  imgs = collaboration.thumbnail_images.map(img => resolveImageUrl(img));
                } 
                else {
                  imgs = (collaboration.images || []).map(img =>
                    getOptimizedImageUrl(img, 'thumbnail')
                  );
                }
                
                const videoThumbSet = new Set(
                  (collaboration.videos || [])
                    .map(v => getProjectVideoThumbnail(v))
                    .filter((t): t is string => Boolean(t))
                );

                const imageItems = (imgs || []).map(img => ({ url: img, isVideo: false }));
                const videoItems = Array.from(videoThumbSet).map(thumb => ({ url: thumb, isVideo: true }));

                const combinedMedia: { url: string; isVideo: boolean }[] = [];
                if (coverImg) {
                  combinedMedia.push({ url: coverImg, isVideo: videoThumbSet.has(coverImg) });
                }

                imageItems.forEach(item => {
                  if (item.url !== coverImg && !combinedMedia.some(m => m.url === item.url)) {
                    combinedMedia.push(item);
                  }
                });

                videoItems.forEach(item => {
                  if (item.url !== coverImg && !combinedMedia.some(m => m.url === item.url)) {
                    combinedMedia.push(item);
                  }
                });

                const allMediaItems = combinedMedia.slice(0, 4);

                if (allMediaItems.length === 0) {
                  return (
                    <div className="w-full h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                      <UserGroupIcon className="h-12 w-12 text-gray-300 dark:text-gray-700" />
                    </div>
                  );
                }

                if (allMediaItems.length === 1) {
                  const item = allMediaItems[0];
                  return (
                    <div className="relative w-full h-full overflow-hidden">
                      <img
                        src={item.url}
                        alt={collaboration.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      {item.isVideo && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                          <div className="w-9 h-9 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20">
                            <PlayIcon className="w-5 h-5 text-white ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                if (allMediaItems.length === 2) {
                  return (
                    <div className="grid grid-cols-2 h-full w-full gap-0.5">
                      {allMediaItems.map((item, i) => (
                        <div key={i} className="relative h-full overflow-hidden">
                          <img
                            src={item.url}
                            alt={`${collaboration.title} ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                          />
                          {item.isVideo && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                              <div className="w-7 h-7 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20">
                                <PlayIcon className="w-4 h-4 text-white ml-0.5" />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }

                if (allMediaItems.length === 3) {
                  const firstItem = allMediaItems[0];
                  return (
                    <div className="grid grid-cols-2 h-full w-full gap-0.5">
                      <div className="relative h-full overflow-hidden">
                        <img
                          src={firstItem.url}
                          alt={`${collaboration.title} 1`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                        />
                        {firstItem.isVideo && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                            <div className="w-7 h-7 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20">
                              <PlayIcon className="w-4 h-4 text-white ml-0.5" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-rows-2 h-full gap-0.5">
                        {allMediaItems.slice(1).map((item, i) => (
                          <div key={i} className="relative h-full overflow-hidden">
                            <img
                              src={item.url}
                              alt={`${collaboration.title} ${i + 2}`}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                            />
                            {item.isVideo && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                                <div className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20">
                                  <PlayIcon className="w-3.5 h-3.5 text-white ml-0.5" />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // 4 images/media
                return (
                  <div className="grid grid-cols-2 grid-rows-2 h-full w-full gap-0.5">
                    {allMediaItems.map((item, i) => (
                      <div key={i} className="relative h-full overflow-hidden">
                        <img
                          src={item.url}
                          alt={`${collaboration.title} ${i + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                        />
                        {item.isVideo && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                            <div className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20">
                              <PlayIcon className="w-3.5 h-3.5 text-white ml-0.5" />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Status Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {/* Project Status - Only for Creator on their own profile */}
                {isOwnProfile && collaboration.creator_id === currentUser?.id && (
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-md shadow-sm border border-white/20 ${
                    collaboration.status === 'open' ? 'bg-black/80 text-white' :
                    collaboration.status === 'pending' ? 'bg-gray-500/80 text-white' :
                    collaboration.status === 'rejected' ? 'bg-gray-900/80 text-white' :
                    collaboration.status === 'in_progress' ? 'bg-black/80 text-white' :
                    'bg-gray-500/80 text-white'
                  }`}>
                    {collaboration.status === 'open' ? 'Live' : collaboration.status.replace('_', ' ')}
                  </span>
                )}

                {/* Collaborator Status - For non-creators who are part of the team */}
                {currentUser && collaboration.creator_id !== currentUser.id && collaboration.collaborators?.find(c => c.user_id === currentUser.id) && (
                  (() => {
                    const myStatus = collaboration.collaborators.find(c => c.user_id === currentUser.id)?.approval_status;
                    return (
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-md shadow-sm border border-white/20 ${
                        myStatus === 'approved' ? 'bg-black/80 text-white' :
                        myStatus === 'rejected' ? 'bg-gray-900/80 text-white' :
                        'bg-gray-500/80 text-white'
                      }`}>
                        {myStatus === 'approved' ? 'Accepted' : myStatus === 'pending' ? 'Invite Pending' : 'Rejected'}
                      </span>
                    );
                  })()
                )}
              </div>

              {/* Paid Badge */}
              {collaboration.is_paid && (
                <div className="absolute top-4 right-4">
                  <span className="bg-white/90 dark:bg-gray-900/90 text-black dark:text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-md shadow-sm border border-white/20 dark:border-gray-800 flex items-center gap-1">
                    <CurrencyRupeeIcon className="h-3 w-3" />
                    Paid
                  </span>
                </div>
              )}

              {/* View Details Overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 bg-white dark:bg-gray-900 px-5 py-2.5 rounded-full flex items-center gap-2 shadow-lg">
                  <EyeIcon className="h-4 w-4 text-black dark:text-white" />
                  <span className="text-sm font-bold text-black dark:text-white">View Project</span>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-5 flex-1 flex flex-col">
              <div className="mb-2 flex justify-between items-start">
                <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-1 group-hover:text-black dark:group-hover:text-white transition-colors">
                  {collaboration.title}
                </h3>
                {isCreator && (
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button 
                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </Menu.Button>
                    <Transition
                      as={React.Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 mt-2 w-36 origin-top-right divide-y divide-gray-100 dark:divide-gray-800 rounded-md bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 border border-gray-100 dark:border-gray-800">
                        <div className="px-1 py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingCollaboration(collaboration);
                                }}
                                className={`${
                                  active ? 'bg-gray-100 dark:bg-gray-800' : ''
                                } group flex w-full items-center rounded-md px-2 py-2 text-sm text-gray-900 dark:text-white`}
                              >
                                <PencilIcon className="mr-2 h-4 w-4" />
                                Edit
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(collaboration.id);
                                }}
                                className={`${
                                  active ? 'bg-red-50 dark:bg-red-900/20' : ''
                                } group flex w-full items-center rounded-md px-2 py-2 text-sm text-red-600 dark:text-red-400`}
                              >
                                <TrashIcon className="mr-2 h-4 w-4" />
                                Delete
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
              </div>

              {/* Tags */}
              {collaboration.tags && collaboration.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {collaboration.tags.slice(0, 3).map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-md text-[10px] font-semibold border border-gray-100 dark:border-gray-700 tracking-wide"
                    >
                      {tag}
                    </span>
                  ))}
                  {collaboration.tags.length > 3 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center font-medium">
                      +{collaboration.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-xs text-gray-600 dark:text-gray-400 mb-4 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                <div className={`flex items-center gap-2 ${!collaboration.is_paid ? 'col-span-2' : ''}`}>
                  <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="font-medium flex flex-wrap text-xs">
                    {(() => {
                      if (!collaboration.duration) return '';
                      const parts = collaboration.duration.split(' ');
                      const chunks = [];
                      for (let i = 0; i < parts.length; i += 2) {
                        if (parts[i+1]) {
                          chunks.push(`${parts[i]} ${parts[i+1]}`);
                        } else {
                          chunks.push(parts[i]);
                        }
                      }
                      return chunks.map((chunk, i) => (
                        <span key={i} className="whitespace-nowrap mr-1 last:mr-0">
                          {chunk}
                        </span>
                      ));
                    })()}
                  </span>
                </div>

                {collaboration.is_paid && (
                  <div className="flex items-center gap-2 justify-end">
                    <CurrencyRupeeIcon className="h-4 w-4 text-gray-900 dark:text-white flex-shrink-0" />
                    <span className="font-bold text-gray-900 dark:text-white text-xs">{formatCurrency(collaboration.budget)}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2 col-span-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="truncate font-medium text-xs text-gray-600 dark:text-gray-400">{collaboration.place}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800 mt-auto">
                <div className="flex items-center gap-2">
                   {collaboration.collaborators && collaboration.collaborators.length > 0 ? (
                     <>
                       <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-wider">Team</span>
                       <div className="flex -space-x-2">
                         {/* Collaborators */}
                         {collaboration.collaborators.slice(0, 3).map((c, i) => (
                           <div key={i} className="relative hover:z-40 transition-all hover:scale-110" style={{ zIndex: 20 - i }} title={c.user?.first_name || c.name || 'Collaborator'}>
                              <Avatar 
                                  src={c.user?.profile_pic_thumb_signed || c.user?.profile_pic || c.profile_pic} 
                                  alt={c.name || 'Collaborator'} 
                                  size="xs" 
                                  className="w-6 h-6 border-2 border-white dark:border-gray-900 ring-1 ring-gray-100 dark:ring-gray-800" 
                              />
                           </div>
                         ))}
                         
                         {collaboration.collaborators.length > 3 && (
                           <div className="relative z-0">
                             <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 border-2 border-white dark:border-gray-900 flex items-center justify-center text-[8px] font-bold text-gray-500 dark:text-gray-400 ring-1 ring-gray-100 dark:ring-gray-800">
                               +{collaboration.collaborators.length - 3}
                             </div>
                           </div>
                         )}
                       </div>
                     </>
                   ) : (
                     <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-wider">No Team Yet</span>
                   )}
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center text-gray-500 dark:text-gray-400 text-xs">
                    <UserGroupIcon className="h-4 w-4 mr-1" />
                    {collaboration.collaborators?.length || 0}
                  </div>
                  <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setShareItem(collaboration);
                    }}
                    className="p-1.5 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-full transition-colors"
                    title="Share"
                  >
                    <ShareIcon className="h-4 w-4" />
                  </button>
                </div>
                <button className="p-2 rounded-full bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 group-hover:bg-black dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-black transition-colors">
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )
        })
        )}
      </div>

      {/* Share Modal */}
      {shareItem && (
        <ShareModal
            isOpen={!!shareItem}
            onClose={() => setShareItem(null)}
            title={shareItem.title}
            url={`${window.location.origin}/collaboration/${shareItem.id}`}
            type="collaboration"
            data={shareItem}
        />
      )}
      {/* Loading Indicator */}
      {(loading || loadingMore) && collaborations.length > 0 && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      )}

      {/* Sentinel for infinite scroll */}
      {hasMore && !loading && (
        <div ref={sentinelRef} className="h-20" />
      )}

      {/* Detail Modal */}
      {selectedCollaboration && (
        <CollaborationShowcaseModal
          collaboration={selectedCollaboration}
          onClose={handleCloseModal}
          onNextCollaboration={handleNextCollaboration}
          onPreviousCollaboration={handlePreviousCollaboration}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
        />
      )}

      {/* Update Modal */}
      {editingCollaboration && (
        <CreateCollaborationModal
          mode="update"
          collaboration={editingCollaboration}
          onClose={() => setEditingCollaboration(null)}
          onSuccess={() => {
            setEditingCollaboration(null);
            if (onUpdate) onUpdate();
          }}
        />
      )}

      <ConfirmationModal
        isOpen={!!collabToDelete}
        onClose={() => setCollabToDelete(null)}
        onConfirm={async () => {
          if (!collabToDelete) return;
          setDeletingCollab(true);
          try {
            await collaborationAPI.deleteCollaboration(collabToDelete);
            toast.success('Project deleted successfully');
            if (onUpdate) onUpdate();
          } catch (error) {
            console.error('Failed to delete project:', error);
            toast.error('Failed to delete project');
          } finally {
            setDeletingCollab(false);
            setCollabToDelete(null);
          }
        }}
        title="Delete Project"
        message={
          <div className="space-y-3">
            <p>Are you sure you want to delete this project? This action cannot be undone.</p>
            <div className="bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 p-3 rounded-xl border border-red-100 dark:border-red-900/30 text-xs font-semibold leading-relaxed">
              ⚠️ Warning: All ratings and reviews submitted for this project (including those given to participating members) will also be permanently deleted.
            </div>
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deletingCollab}
      />
    </>
  );
};

export default CollaborationGrid;
